import { Trans, useTranslation } from 'react-i18next';
import useInvalidateFetchAllProperties from 'components/domain/property/useInvalidateFetchAllProperties';
import useAppModal from 'hooks/useAppModal';
import useAppMutation from 'hooks/useAppMutation';
import API from 'services/API';
import { modalIds } from 'components/modal/Modal.constants';
import useNotify from 'hooks/useNotify';
import { useQueryClient } from '@tanstack/react-query';
import { PROPERTIES_FOR_PROPERTIES_BODY_BASE_QUERY_KEY } from 'pages/agencyProperties/body/useFetchPropertiesForPropertiesBody';
import { TransLink } from 'components/text/TransLink';
import { ConfirmationModalProps } from './Confirmation.types';
import { PropertyNameStyled } from './Confirmation.styles';

interface DeleteModalProps extends ConfirmationModalProps {
  isHotel: boolean;
  isMasterUnit: boolean;
  isUnitType: boolean;
}

const useDeleteModal = () => {
  const queryClient = useQueryClient();
  const { notifySuccess } = useNotify();
  const { t } = useTranslation();
  const { openConfirmModal, closeModal, setErrorConfirmModal } = useAppModal();
  const { invalidateFetchAllProperties } = useInvalidateFetchAllProperties();

  const closeConfirmationModal = () => {
    closeModal(modalIds.confirm);
  };

  const { mutateAsync } = useAppMutation(
    ['deleteProperty'],
    async ({
      uid,
      isHotel,
      isUnitType,
    }: {
      uid: string;
      name: string;
      isHotel: boolean;
      isUnitType: boolean;
    }) => {
      let url = '';

      if (isHotel) {
        url = `/api/internal/hotels/${uid}`;
      } else if (isUnitType) {
        url = `/v3/properties/${uid}`;
      } else {
        url = `/api/v3/properties/${uid}`;
      }

      const response = await API.delete(url);
      return response.data;
    },
    {
      onSuccess: (_, { name }) => {
        notifySuccess(
          t('pageProperties.actions.deletionSuccess', {
            name: name.length > 30 ? `${name.slice(0, 30)}...` : name,
          }),
        );
        closeConfirmationModal();
        queryClient.invalidateQueries(
          [PROPERTIES_FOR_PROPERTIES_BODY_BASE_QUERY_KEY],
          { exact: false },
        );
        invalidateFetchAllProperties();
      },
      onError: (error: any) => {
        setErrorConfirmModal(error.response.data.apiErrorMessage);
      },
    },
  );

  const handleDelete = (
    uid: string,
    name: string,
    isHotel: boolean,
    isUnitType: boolean,
  ) =>
    new Promise<void>((resolve) => {
      mutateAsync({ uid, name, isHotel, isUnitType }).then(resolve);
    });

  const openDeleteModal = ({
    uid,
    name,
    isHotel,
    isMasterUnit,
    isUnitType,
  }: DeleteModalProps) => {
    openConfirmModal({
      body: (
        <>
          {t('pageProperties.actions.deletePropertyConfirmation')}{' '}
          <PropertyNameStyled>{name}</PropertyNameStyled>?
          <p>
            <Trans
              i18nKey="pageProperties.actions.deletePropertyWarning"
              components={{
                br: <br />,
                b: <b />,
                link1: (
                  <TransLink
                    target="_blank"
                    data-testid="deleting-property-documentation"
                    href="https://help.hostfully.com/en/articles/3949529-export-extended-data"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </p>
          {isHotel && <p>{t('pageProperties.actions.deleteHotelUnits')}</p>}
          {isMasterUnit && (
            <p>{t('pageProperties.actions.deleteMasterSubUnits')}</p>
          )}
        </>
      ),
      alertMessage: isUnitType
        ? t('pageProperties.actions.deleteUnitType')
        : undefined,
      onConfirmAsync: () => handleDelete(uid, name, isHotel, isUnitType),
      title: t('pageProperties.actions.common.areYouSure'),
      confirmLabel: t('pageProperties.actions.delete'),
      confirmColor: 'danger',
    });
  };

  return {
    openDeleteModal,
  };
};

export default useDeleteModal;
